// Media-upload helper for matrix-js-sdk that injects the gemSpec_TI-M_Basis
// `X-TIM-User-Agent` header. Without it, the gematik SBG returns 403
// M_FORBIDDEN before the request reaches Synapse.
//
// Why this exists: matrix-js-sdk's MatrixHttpApi.uploadContent (v41,
// node_modules/matrix-js-sdk/lib/http-api/index.js:48-150) takes the XHR
// branch in browsers and bypasses fetchFn entirely, so the customFetch on
// MatrixService never sees the upload. We mirror that XHR branch and add
// the missing header. matrixService.js installs this as an override on
// every freshly-created client.
//
// Behaviour mirrors the SDK's XHR upload path: 30s stall timer re-armed on
// progress, abortController-driven cancel, parsed { content_uri } on 2xx,
// MatrixError-shaped reject on >=400.

import { buildTIMUserAgent } from '../utils/timUserAgent';
import * as credentialStore from './credentialStore';

const STALL_TIMEOUT_MS = 30000;

function resolveHomeserverUrl(client) {
  const fromClient = client?.getHomeserverUrl?.();
  if (fromClient) return fromClient;
  // Raw (non-validating) read: a partial blob is still a usable fallback here.
  const creds = credentialStore.readCredentials();
  if (creds?.homeserverUrl) return creds.homeserverUrl;
  return null;
}

function resolveAccessToken(client) {
  const fromClient = client?.getAccessToken?.();
  if (fromClient) return fromClient;
  // Raw (non-validating) read: a partial blob is still a usable fallback here.
  const creds = credentialStore.readCredentials();
  if (creds?.accessToken) return creds.accessToken;
  return null;
}

export function uploadMedia(client, file, opts = {}) {
  const includeFilename = opts.includeFilename !== undefined ? opts.includeFilename : true;
  const abortController = opts.abortController || new AbortController();
  const contentType = opts.type || file.type || 'application/octet-stream';
  const fileName = opts.name !== undefined ? opts.name : file.name;
  const progressHandler = typeof opts.progressHandler === 'function' ? opts.progressHandler : null;

  return new Promise((resolve, reject) => {
    const homeserverUrl = resolveHomeserverUrl(client);
    if (!homeserverUrl) {
      reject(new Error('uploadMedia: missing homeserver URL'));
      return;
    }

    let url;
    try {
      url = new URL('/_matrix/media/v3/upload', homeserverUrl);
    } catch (err) {
      reject(new Error(`uploadMedia: invalid homeserver URL: ${err.message}`));
      return;
    }
    if (includeFilename && fileName) {
      url.searchParams.set('filename', encodeURIComponent(fileName));
    }

    const xhr = new XMLHttpRequest();
    let stallTimer = null;
    let settled = false;

    const clearStall = () => {
      if (stallTimer !== null) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        try { xhr.abort(); } catch { /* noop */ }
        reject(new Error('Upload stalled (no progress for 30s)'));
      }, STALL_TIMEOUT_MS);
    };

    const onAbort = () => {
      if (settled) return;
      settled = true;
      clearStall();
      try { xhr.abort(); } catch { /* noop */ }
      reject(new DOMException('Aborted', 'AbortError'));
    };
    abortController.signal.addEventListener('abort', onAbort);

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.LOADING || xhr.readyState === XMLHttpRequest.HEADERS_RECEIVED) {
        armStall();
        return;
      }
      if (xhr.readyState !== XMLHttpRequest.DONE) return;
      if (settled) return;
      settled = true;
      clearStall();
      abortController.signal.removeEventListener('abort', onAbort);

      if (xhr.status === 0) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      if (xhr.status >= 400) {
        let errcode;
        let body;
        try {
          body = JSON.parse(xhr.responseText);
          errcode = body?.errcode;
        } catch {
          // non-JSON error body
        }
        const err = new Error(body?.error || `Upload failed with status ${xhr.status}`);
        err.httpStatus = xhr.status;
        err.errcode = errcode;
        err.data = body;
        reject(err);
        return;
      }
      try {
        const parsed = JSON.parse(xhr.responseText || '');
        resolve(parsed);
      } catch (err) {
        reject(new Error(`Failed to parse upload response: ${err.message}`));
      }
    };

    xhr.onerror = () => {
      if (settled) return;
      settled = true;
      clearStall();
      abortController.signal.removeEventListener('abort', onAbort);
      reject(new Error('Network error during upload'));
    };
    xhr.ontimeout = () => {
      if (settled) return;
      settled = true;
      clearStall();
      abortController.signal.removeEventListener('abort', onAbort);
      reject(new Error('Network error during upload'));
    };

    if (progressHandler) {
      xhr.upload.onprogress = (ev) => {
        armStall();
        progressHandler({ loaded: ev.loaded, total: ev.total });
      };
    } else {
      xhr.upload.onprogress = () => {
        armStall();
      };
    }

    xhr.open('POST', url.href);
    const accessToken = resolveAccessToken(client);
    if (accessToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    }
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('X-TIM-User-Agent', buildTIMUserAgent());

    xhr.send(file);
    armStall();
  });
}
